import React, { useState, useMemo, useCallback, useEffect } from "react";


// Context
import { MasteryMapContext } from "./context";

// Components
import Widget from "./Widget";
import Modal from "./Modal";

// Modals
import GenerateModal from "../../Generate/Modal";
import QuizSetupModal from "../../components/QuizSetupModal";
import ProcessingModal from "../../components/ProcessingModal";

// Utils
import * as urls from "../../utils/urls";
import useCSRFToken from "../../utils/hooks/csfrToken";

const MasteryMap = ({ courseId }) => {
  // CSRF Token
  const csrfToken = useCSRFToken();

  const [showModal, setShowModal] = useState(false);
  const [units, setUnits] = useState([]);
  const [subUnitsMap, setSubUnitsMap] = useState({});

  // Flags
  const [processing, setProcessing] = useState(false);

  // Generate Modal
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateType, setGenerateType] = useState(null);

  // Quiz Setup Modal
  const [showQuizSetupModal, setShowQuizSetupModal] = useState(false);

  const loadUnits = useCallback(async () => {
    try {
      const options = { method: "GET", headers: { "Accept": "application/json" } };
      const response = await fetch(urls.unitsUrl(courseId), options);
      if (response.ok) {
        const data = await response.json();

        // Use mock data if response is empty
        if (!data || data.length === 0) {
          setUnits(data['data']);
        }
      }

      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [courseId]);

  const getSubUnits = useCallback(async (unitId) => {
    try {
      const options = { method: "GET", headers: { "Accept": "application/json" } };

      const response = await fetch(urls.subUnitsUrl(courseId, unitId), options);
      if (response.ok) {
        const data = await response.json();

        // Use mock data if response is empty
        setSubUnitsMap((prev) => ({ ...prev, [unitId]: data['data'] }));
      }
    } catch (error) {
      console.error(error);
    }
  }, [courseId]);

  const handleGenerate = useCallback((type) => {
    setGenerateType(type);
    setShowGenerateModal(true);
  }, []);

  const handleCloseGenerateModal = useCallback(() => {
    setShowGenerateModal(false);
    setGenerateType(null);
  }, []);

  const handleOpenGenerateQuizModal = useCallback(() => {
    setShowQuizSetupModal(true);
  }, []);

  const handleGenerateQuiz = useCallback(async (data) => {
    setShowQuizSetupModal(false);
    setProcessing(true);

    try {
      const formData = new FormData();
      // TODO: Keith/Dmytro/Alex -> Remove this hardcoded values
      formData.append('quiz[language]', 'en')
      formData.append('quiz[difficulty]', 'easy')
      formData.append('quiz[multiple_choice]', 'true')
      formData.append('quiz[max_questions]', '5')

      // TODO: Keith/Dmytro/Alex -> Implement quiz generation with the data from the quiz setup modal
      // formData.append('quiz[time_limit]', data.timeLimit?.toString() || '');
      // formData.append('quiz[questions]', data.questions.join(','));
      // formData.append('quiz[adaptive_mix]', data.adaptiveMix ? 'true' : 'false');
      // formData.append('quiz[skipping_hints]', data.skippingHints ? 'true' : 'false');

      const url = urls.aiQuizzesUrl();
      const options = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      }
      const response = await fetch(url, options);
      if (response.ok) {
        // Navigate to the quiz Page
        const quiz = await response.json();
        Turbo.visit(urls.quizUrl(quiz.data.id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  }, [csrfToken]);

  const handleCloseQuizSetupModal = useCallback(() => {
    setShowQuizSetupModal(false);
    setQuizSetupType(null);
  }, []);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  // Context
  const context = useMemo(() => ({
    showModal,
    setShowModal,
    // Data
    units,
    subUnitsMap,
    // Methods
    getSubUnits,
    // Modals
    onGenerateModal: handleGenerate,
    onGenerateQuizModal: handleOpenGenerateQuizModal,
  }), [showModal, loadUnits, units, subUnitsMap, handleGenerate]);

  return (
    <MasteryMapContext.Provider value={context}>
      <Widget />

      <Modal />

      <GenerateModal
        open={showGenerateModal}
        onClose={handleCloseGenerateModal}
        type={generateType}
      />

      <QuizSetupModal
        open={showQuizSetupModal}
        onClose={handleCloseQuizSetupModal}
        onGenerate={handleGenerateQuiz}
      />

      <ProcessingModal open={processing} />
    </MasteryMapContext.Provider>
  );
};

export default MasteryMap;
